import { useState, Fragment, forwardRef, useMemo, ChangeEvent } from "react";
import {
    Box,
    Stack,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Collapse,
    Typography,
    Divider,
    Dialog,
    AppBar,
    Toolbar,
    Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { ExpandMore, ExpandLess, Close } from "@mui/icons-material";
import { startCase, isObject } from "lodash";
import { CalendarRule, Program } from "src/modules/auth/types";

import { useAppSelector } from "src/hooks";
import { navigatorSelector } from "src/modules/auth/redux/authSlice";
import { tripsAPI } from "src/modules/trips/redux/tripSlice";
import { useFormikContext } from "formik";
import { TripRequestFormValues } from "../types";
import { enqueueSnackbar } from "notistack";
import ServiceSelector from "./ServiceSelector";
import GoogleMap from "src/components/map/GoogleMap";
import moment from "moment";

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
    programs: Program[];
    open: boolean;
    onClose: VoidFunction;
}

// doesnt need to be a closure
function getProgram(name = "", programs: Program[] = [], skipCalendar = false) {
    let item = programs.find((item) => item.programName === name);
    // programCalendarRules is a JSON string so we have to parse it
    if (
        !skipCalendar &&
        item &&
        item.programCalendarRules &&
        isObject(JSON.parse(item.programCalendarRules))
    ) {
        return {
            ...item,
            parsedProgramCalendarRules: JSON.parse(item.programCalendarRules),
        };
    }
    return item;
}

const ProgramDetails = ({ programs, open, onClose }: Props) => {
    const [showRules, setShowRules] = useState(true);
    const isNavigator = useAppSelector(navigatorSelector);
    const { values } = useFormikContext<TripRequestFormValues>();
    const [selected, setSelected] = useState(values.tripProgram);

    const [getMapGeofence, { isLoading, data: mapGeofence }] =
        tripsAPI.useLazyGetMapGeofenceQuery();

    function toggleColapse() {
        setShowRules(!showRules);
    }

    const program = useMemo(
        () => getProgram(selected, programs),
        [programs, selected]
    );

    async function handleTripProgram(program: string) {
        try {
            setSelected(program);
            const zones = getProgram(program, programs)?.activeGeozoneIds;
            if (!zones?.length) return;
            await getMapGeofence(zones).unwrap();
        } catch {
            enqueueSnackbar("Sorry an error occured", { variant: "error" });
        }
    }

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <AppBar color="primary" sx={{ position: "relative" }}>
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div">
                        {isNavigator ? "Service" : "Program"} Details
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Box p={{ xs: 2, md: 4 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <Typography variant="h4" component="h2" flexGrow={3}>
                        {program?.programName}
                        {program?.membershipID && ` (${program.membershipID})`}
                    </Typography>

                    <Box flexGrow={1}>
                        <ServiceSelector
                            label={isNavigator ? "Service" : "Program"}
                            name="serviceDetails"
                            programs={programs}
                            onChange={(evt: ChangeEvent<any>) => {
                                handleTripProgram(evt.target.value);
                            }}
                        />
                    </Box>
                </Stack>

                <Grid container spacing={2}>
                    {/* Details  */}
                    <Grid item xs={12} md={6}>
                        <List>
                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Description"
                                    secondary={
                                        program?.programDescription || "N/A"
                                    }
                                />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Archived"
                                    secondary={program?.archived ? "Yes" : "No"}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Membership"
                                    secondary={program?.membershipID || "N/A"}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem
                                disableGutters
                                sx={{
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                }}
                            >
                                <ListItemText
                                    primary="Fixed Fee"
                                    sx={{ width: "40%" }}
                                />

                                <ListItemText sx={{ width: "60%" }}>
                                    {program && program.fixedFee
                                        ? Object.keys(program?.fixedFee)?.map(
                                              (item, index) => (
                                                  <Typography key={index}>
                                                      <span>
                                                          {startCase(item)}
                                                      </span>
                                                      :{" "}
                                                      <strong>
                                                          {program?.fixedFee?.[
                                                              item
                                                          ] || "N/A"}
                                                      </strong>
                                                  </Typography>
                                              )
                                          )
                                        : "N/A"}
                                </ListItemText>
                            </ListItem>
                            <Divider />

                            <ListItem
                                disableGutters
                                sx={{
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                }}
                            >
                                <ListItemText
                                    primary="Per mile Fee"
                                    sx={{ width: "40%" }}
                                />

                                <ListItemText sx={{ width: "60%" }}>
                                    {program && program.perMileFee
                                        ? Object.keys(program.perMileFee)?.map(
                                              (item, index) => (
                                                  <Typography key={index}>
                                                      <span>
                                                          {startCase(item)}
                                                      </span>
                                                      :{" "}
                                                      <strong>
                                                          {program
                                                              ?.perMileFee?.[
                                                              item
                                                          ] || "N/A"}
                                                      </strong>
                                                  </Typography>
                                              )
                                          )
                                        : "N/A"}
                                </ListItemText>
                            </ListItem>

                            {program &&
                                isObject(
                                    program.parsedProgramCalendarRules
                                ) && (
                                    <Fragment>
                                        <Divider />
                                        <ListItemButton
                                            onClick={toggleColapse}
                                            disableGutters
                                        >
                                            <ListItemText primary="Program Calendar Rules" />

                                            {showRules ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )}
                                        </ListItemButton>

                                        <Collapse
                                            in={showRules}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List>
                                                {Object.keys(
                                                    program?.parsedProgramCalendarRules
                                                )?.map((item, index) => {
                                                    const rule:
                                                        | CalendarRule
                                                        | string = isObject(
                                                        program?.parsedProgramCalendarRules
                                                    )
                                                        ? (program as any)
                                                              ?.parsedProgramCalendarRules?.[
                                                              item
                                                          ]
                                                        : item;

                                                    return (
                                                        <ListItem
                                                            key={index}
                                                            disableGutters
                                                            divider
                                                            sx={{
                                                                alignItems:
                                                                    "flex-start",
                                                                justifyContent:
                                                                    "space-between",
                                                            }}
                                                        >
                                                            <ListItemText
                                                                sx={{
                                                                    width: "40%",
                                                                }}
                                                                primary={startCase(
                                                                    item
                                                                )}
                                                            />
                                                            <ListItemText
                                                                sx={{
                                                                    width: "60%",
                                                                }}
                                                            >
                                                                {isObject(
                                                                    rule
                                                                ) ? (
                                                                    <Fragment>
                                                                        <Typography>
                                                                            <span>
                                                                                Active
                                                                            </span>
                                                                            :{" "}
                                                                            <strong>
                                                                                {rule?.Active
                                                                                    ? "Yes"
                                                                                    : "No"}
                                                                            </strong>
                                                                        </Typography>

                                                                        <Typography>
                                                                            <span>
                                                                                Start
                                                                            </span>
                                                                            :{" "}
                                                                            <strong>
                                                                                {moment(
                                                                                    rule?.StartJSON
                                                                                ).format(
                                                                                    "LLLL"
                                                                                )}
                                                                            </strong>
                                                                        </Typography>

                                                                        <Typography>
                                                                            <span>
                                                                                End
                                                                            </span>
                                                                            :{" "}
                                                                            <strong>
                                                                                {moment(
                                                                                    rule?.EndJSON
                                                                                ).format(
                                                                                    "LLLL"
                                                                                )}
                                                                            </strong>
                                                                        </Typography>
                                                                    </Fragment>
                                                                ) : (
                                                                    rule
                                                                )}
                                                            </ListItemText>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>
                                    </Fragment>
                                )}
                        </List>
                    </Grid>
                    {/* Map preview for program geozones */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justityContent: "center",
                        }}
                    >
                        <GoogleMap
                            geofence={mapGeofence}
                            loading={isLoading}
                            pickUpCoords={{
                                lat: values.pickUpPlace.latitude,
                                lng: values.pickUpPlace.longitude,
                            }}
                            dropOffCoords={{
                                lat: values.dropOffPlace.latitude,
                                lng: values.dropOffPlace.longitude,
                            }}
                            sx={{
                                minHeight: 450,
                                height: "100%",
                                width: "100%",
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Dialog>
    );
};

export default ProgramDetails;
